import { createContext, useContext, useEffect, useRef, useMemo, useCallback, type PropsWithChildren } from "react";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { useAuthContext } from "@acto/web-common/auth";
import type { PlatformName } from "@acto/taskpane/core/platform/types";
import type { AnalyticsContext, AnalyticsEventName, AnalyticsEventProperties } from "./types";
import { detectDevice } from "./detectDevice";
import { isInternalUser } from "./isInternalUser";

// Maps internal plugin names to user-facing platform labels sent to Segment.
const PLATFORM_DISPLAY_NAMES: Record<PlatformName, string | undefined> = {
  office: "outlook",
  dynamicsIframe: "dynamics",
  salesforce: "salesforce",
  c4c: "sap-c4c",
  web: "web",
  noop: undefined,
};

const AnalyticsContextInstance = createContext<AnalyticsContext | null>(null);

const SEGMENT_WRITE_KEY = import.meta.env["VITE_SEGMENT_WRITE_KEY"] as string | undefined;
const BUILD_VERSION = import.meta.env["VITE_BUILD_VERSION"] as string | undefined;

/**
 * Console-based analytics for development/testing when Segment is not configured.
 * Logs all events to console with a distinctive prefix.
 */
const createConsoleAnalytics = () => ({
  track: (eventName: string, properties: Record<string, unknown>) => {
    console.log("[Analytics] track:", eventName, properties);
  },
  identify: (userId: string, traits?: Record<string, unknown>) => {
    console.log("[Analytics] identify:", userId, traits);
  },
});

interface AnalyticsProviderProps extends PropsWithChildren {
  app: "widget" | "management";
  platform?: PlatformName;
}

/**
 * Provides `track`/`identify`, backed by Segment when `VITE_SEGMENT_WRITE_KEY` is set and a
 * console logger otherwise. Identifies each user once after the tenant resolves and adds the
 * common context properties to every tracked event.
 */
export function AnalyticsProvider({ children, app, platform }: AnalyticsProviderProps) {
  const { user, tenantName } = useAuthContext();
  const analyticsRef = useRef<AnalyticsBrowser | null>(null);
  const consoleAnalyticsRef = useRef(createConsoleAnalytics());
  const identifiedUserIdRef = useRef<string | null>(null);
  const device = useMemo(() => detectDevice(), []);
  const isInternal = isInternalUser(user?.email);

  // Initialize Segment or use console fallback
  const isSegmentEnabled = Boolean(SEGMENT_WRITE_KEY);

  useEffect(() => {
    if (SEGMENT_WRITE_KEY && !analyticsRef.current) {
      analyticsRef.current = AnalyticsBrowser.load({
        writeKey: SEGMENT_WRITE_KEY,
      });
    }
  }, []);

  // Auto-identify user when they log in, waiting for tenantName to be available.
  useEffect(() => {
    if (user && tenantName && user.id !== identifiedUserIdRef.current) {
      const traits = {
        email: user.email,
        name: user.name,
        tenantId: user.tenantId,
        tenantName,
        roles: user.roles,
        buildVersion: BUILD_VERSION,
        app,
        device,
        isInternal,
      };

      if (isSegmentEnabled && analyticsRef.current) {
        void analyticsRef.current.identify(user.id, traits);
      } else {
        consoleAnalyticsRef.current.identify(user.id, traits);
      }

      identifiedUserIdRef.current = user.id;
    }
  }, [user, tenantName, isSegmentEnabled, app, device, isInternal]);

  const track = useCallback(
    (eventName: AnalyticsEventName, properties: AnalyticsEventProperties) => {
      // Add common properties to all events
      const enrichedProperties = {
        ...properties,
        tenantId: user?.tenantId,
        tenantName: tenantName || undefined,
        buildVersion: BUILD_VERSION,
        platform: platform ? PLATFORM_DISPLAY_NAMES[platform] : undefined,
        app,
        device,
        isInternal,
        // Frame size at event time, not screen size — the widget renders in host taskpanes/iframes,
        // so this is the space the layout actually gets.
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      };

      if (isSegmentEnabled && analyticsRef.current) {
        void analyticsRef.current.track(eventName, enrichedProperties);
      } else {
        consoleAnalyticsRef.current.track(eventName, enrichedProperties);
      }
    },
    [isSegmentEnabled, user?.tenantId, tenantName, platform, app, device, isInternal],
  );

  const identify = useCallback(
    (userId: string, traits?: Record<string, unknown>) => {
      const enrichedTraits = { ...traits, isInternal };
      if (isSegmentEnabled && analyticsRef.current) {
        void analyticsRef.current.identify(userId, enrichedTraits);
      } else {
        consoleAnalyticsRef.current.identify(userId, enrichedTraits);
      }
      identifiedUserIdRef.current = userId;
    },
    [isSegmentEnabled, isInternal],
  );

  const contextValue = useMemo<AnalyticsContext>(
    () => ({
      track,
      identify,
      isReady: isSegmentEnabled ? Boolean(analyticsRef.current) : true,
    }),
    [track, identify, isSegmentEnabled],
  );

  return <AnalyticsContextInstance.Provider value={contextValue}>{children}</AnalyticsContextInstance.Provider>;
}

/**
 * Hook to access analytics context.
 * Throws if used outside of AnalyticsProvider.
 */
export function useAnalytics(): AnalyticsContext {
  const context = useContext(AnalyticsContextInstance);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
}
